/*
 * Copyright (c) Dito Technologies LLC
 */

import React from "react";
import styles from "ui/feature/piece-play/common/piece-renderer/PieceRendererNote.module.scss";
import {createFragmentContainer} from "react-relay";
import {graphql} from "babel-plugin-relay/macro";
import {PieceRendererNote_note$data} from "ui/feature/piece-play/common/__generated__/PieceRendererNote_note.graphql";
import PieceRendererNoteTooltip from "ui/feature/piece-play/common/piece-renderer/PieceRendererNoteTooltip";
import MIDIToLineFromTrebleClef from "util/MIDIToLineFromTrebleClef";
import {MIDIToPitchName} from "util/pitch";
import {useDrag} from 'react-dnd'
import DndItemTypes, {NoteItem} from "ui/feature/piece-play/common/DndItemTypes";
import PieceRendererNoteDisplay from "ui/feature/piece-play/common/piece-renderer/PieceRendererNoteDisplay";
import {getEmptyImage} from "react-dnd-html5-backend";
import {NoteDisplayState} from "ui/feature/piece-play/common/PlayTypes";

const noteHeight = 24;
const lineHeight = 3;
const inBetweenLineSpacing = 24;
export const widthForWholeNote = 200;

interface Props {
    note: PieceRendererNote_note$data;
    noteState: NoteDisplayState
    pieceId: string;
}

export function TopPxForNote(midi: number, lineAdjustment: number = 0): number {
    const lineIdxRelativeToTopOfRightHandStaff = (MIDIToLineFromTrebleClef(midi) || 0) + lineAdjustment;
    const fullLinesVertical = Math.floor(lineIdxRelativeToTopOfRightHandStaff / 2);
    let output = fullLinesVertical * (lineHeight + inBetweenLineSpacing);
    if (Math.abs(lineIdxRelativeToTopOfRightHandStaff) % 2 === 1) {
        // we are in the middle the bar
        output += (inBetweenLineSpacing / 2) + (lineHeight / 2);
    }
    return output - (noteHeight / 2) + (lineHeight / 2); // 105 is the padding from the first clef to the top
}

const PieceRendererNote: React.FC<Props> = ({note, pieceId, noteState}) => {

    const [detailOpen, setDetailOpen] = React.useState<boolean>(false)

    const dndItem: NoteItem = {
        bulbId: note.bulb_id,
        midiNumber: note.midi_number,
        noteStart: note.note_start,
        noteDuration: note.note_duration
    }

    const [{isDragging}, drag, preview] = useDrag(() => ({
        type: DndItemTypes.NOTE,
        item: dndItem,
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    }));

    const top = React.useMemo(() => {
        return TopPxForNote(note.midi_number);
    }, [note.midi_number]);

    const left = React.useMemo(() => {
        return (widthForWholeNote * note.note_start)
    }, [note.note_start]);

    const onNoteEnter = React.useCallback(() => {
        setDetailOpen(true);
    }, [setDetailOpen]);

    const onNoteLeave = React.useCallback(() => {
        setDetailOpen(false);
    }, [setDetailOpen]);

    React.useEffect(() => {
        preview(getEmptyImage(), {captureDraggingState: true})
    }, [preview])

    return (
        <div onMouseLeave={onNoteLeave} onMouseEnter={onNoteEnter} style={{}}>
            {detailOpen && !isDragging && <div style={{
                left: `${left}px`,
                top: `${top + noteHeight}px`,
            }} className={styles.tooltip}>
                <PieceRendererNoteTooltip pieceId={pieceId} note={note}/>
            </div>}
            <PieceRendererNoteDisplay
                onClick={onNoteEnter}
                dragRef={drag}
                midiNumber={note.midi_number}
                lineAdjustment={undefined}
                noteDuration={note.note_duration}
                noteStart={note.note_start}
                isDragging={isDragging}
                notePitchTitle={MIDIToPitchName(note.midi_number)}
                noteState={noteState}
            />
        </div>
    );
}

export default createFragmentContainer(PieceRendererNote, {
    note: graphql`
    fragment PieceRendererNote_note on MusicPieceFileNote {
        bulb_id
        note_start
        midi_number
        note_duration
        ...PieceRendererNoteTooltip_note
    }
    `
});